import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api, { fmtErr } from "../api";
import { useAuth } from "../AuthContext";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { LegalLinks } from "../components/LegalLinks";

const HERO = "https://images.unsplash.com/photo-1526392587392-d1627b6c134a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxsYXduJTIwbW93ZXIlMjBmcmVzaCUyMGdyYXNzfGVufDB8fHx8MTc4NzQxMjE5NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("signup") === "1" ? "register" : "login"); // login | register | forgot
  const [form, setForm] = useState({ email: "", password: "", name: "", role: searchParams.get("signup") === "1" ? "operator" : "owner", invite_code: "", phone: "" });
  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const go = (u) => {
    setUser(u);
    // honor the page the user was trying to reach (email deep links: View & pay, Review quote…)
    const target = loc.state?.from?.pathname;
    const fallback = u.role === "owner" ? "/owner" : u.role === "subcontractor" ? "/sub" : "/";
    nav(target && target.startsWith("/") && target !== "/login" ? target : fallback, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setSent(true);
      } else {
        const r = mode === "login"
          ? await api.post("/auth/login", { email: form.email, password: form.password })
          : await api.post("/auth/register", form);
        go(r.data);
      }
    } catch (e2) { setErr(fmtErr(e2)); }
    setBusy(false);
  };

  const switchMode = (m) => { setMode(m); setErr(""); setSent(false); };

  return (
    <div className="relative flex min-h-screen flex-col">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO})` }} />
      <div className="absolute inset-0 bg-black/65" />
      <div className="relative z-10 mx-auto flex w-full max-w-lg flex-1 flex-col justify-end p-6 pb-10">
        <div className="mb-8 flex items-center gap-3">
          <img src="/veloktify-icon.svg" alt="Veloktify" className="h-12 w-12 rounded-lg" />
          <div>
            <h1 className="font-heading text-4xl font-bold tracking-tight text-white" data-testid="login-title">RPM Field</h1>
            <p className="text-sm font-semibold text-[hsl(196,72%,56%)]">by Veloktify — Move fast. Stay in control.</p>
          </div>
        </div>
        <form onSubmit={submit} className="card-flat space-y-4 p-6" data-testid="login-form">
          {mode === "register" && (
            <>
              <input data-testid="register-name" className="field-input w-full" placeholder="Your name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input data-testid="register-phone" type="tel" className="field-input w-full" placeholder="Mobile (optional — for calls/text alerts)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div className="flex gap-2">
                {[["operator", "Operator"], ["owner", "Owner"], ["subcontractor", "Sub"]].map(([r, label]) => (
                  <button type="button" key={r} data-testid={`role-${r}`} onClick={() => setForm({ ...form, role: r })}
                    className={`h-12 flex-1 rounded-md border text-xs font-semibold transition-colors ${form.role === r ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground" data-testid="role-explainer">
                {form.role === "operator" && "You run a property maintenance company — manage jobs, crew, quoting and invoicing for your customers. Free for 30 days, no card to start — then $29 CAD/month for your first 12 months (introductory), then $79 CAD/month or $790 CAD/year."}
                {form.role === "owner" && "You own a property and your maintenance company uses RPM Field — approve quotes, see visit photos, and pay invoices here. You'll need the operator invite code from your property maintenance company."}
                {form.role === "subcontractor" && "You're crew — you receive job offers and complete work. You'll need the operator invite code from the company you work for."}
              </p>
              {form.role !== "operator" && (
                <input data-testid="register-invite-code" className="field-input w-full uppercase" placeholder="Operator invite code (from your maintenance company)" value={form.invite_code}
                  onChange={(e) => setForm({ ...form, invite_code: e.target.value })} required />
              )}
            </>
          )}
          <input data-testid="login-email" type="email" className="field-input w-full" placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          {mode !== "forgot" && (
            <div className="relative">
              <input data-testid="login-password" type={showPw ? "text" : "password"} className="field-input w-full pr-12" placeholder="Password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw(!showPw)} aria-label="Show password"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground">
                {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          )}
          {mode === "forgot" && sent && (
            <p data-testid="forgot-sent" className="rounded-md bg-primary/10 p-3 text-sm font-semibold text-primary">
              If that email is registered, a reset link is on its way. Check your inbox — the link expires in 1 hour.
            </p>
          )}
          {err && <p data-testid="login-error" className="text-sm font-semibold text-destructive">{err}</p>}
          {mode === "register" && (
            <p className="text-center text-xs text-muted-foreground" data-testid="terms-consent">
              By creating an account you agree to our{" "}
              <a href="https://veloktify.com/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline underline-offset-2">Terms of Service</a>
              {" "}and{" "}
              <a href="https://veloktify.com/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline underline-offset-2">Privacy Policy</a>.
            </p>
          )}
          <button data-testid="login-submit" disabled={busy || (mode === "forgot" && sent)} className="btn-primary-big !h-14 !text-lg disabled:opacity-40">
            {busy ? "One sec…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Email me a reset link"}
          </button>
          {mode === "login" && (
            <button type="button" data-testid="forgot-password-link" onClick={() => switchMode("forgot")}
              className="w-full text-center text-sm font-semibold text-muted-foreground">
              Forgot your password?
            </button>
          )}
          <button type="button" data-testid="toggle-auth-mode" onClick={() => switchMode(mode === "login" ? "register" : "login")}
            className="w-full text-center text-sm font-semibold text-primary">
            {mode === "login" ? "New here? Create an account" : "Have an account? Sign in"}
          </button>
        </form>
        <div className="mt-6">
          <LegalLinks light />
        </div>
      </div>
    </div>
  );
}
